import { EntityPermissionType, EntityType } from "@amzn/ask-legal-domain";
import { Link, Popover, SpaceBetween, TextContent } from "@amzn/awsui-components-react";
import * as React from "react";

/**
 * Short, admin-facing explanations of what each permission level grants.
 * Rendered as an "Info" popover next to the role's form field label so that
 * admins can understand a permission level without leaving the portal.
 */
const PERMISSION_LEVEL_DETAILS: Record<EntityPermissionType, { summary: string; capabilities: string[] }> = {
    [EntityPermissionType.Admin]: {
        summary: "Full control of the instance.",
        capabilities: [
            "Manage instance settings, navigation, roles, and recommendations",
            "Grant or revoke permissions for other users and teams",
            "Create, edit, approve, and publish all content",
            "Everything an Approver, Editor, and Viewer can do"
        ]
    },
    [EntityPermissionType.Approver]: {
        summary: "Reviews and publishes content changes.",
        capabilities: [
            "Approve and publish drafts submitted by Editors",
            "Create and edit content",
            "Everything an Editor and Viewer can do"
        ]
    },
    [EntityPermissionType.Editor]: {
        summary: "Creates and edits content.",
        capabilities: [
            "Create and edit pages and drafts",
            "View all content",
            "Cannot manage permissions or instance settings"
        ]
    },
    [EntityPermissionType.Viewer]: {
        summary: "Read-only access.",
        capabilities: [
            "View published content",
            "Cannot create, edit, or manage anything"
        ]
    }
};

/**
 * Scope-specific overrides. At the Instance level, "Viewer" is the baseline
 * "can enter this instance" grant rather than a pure content-read role, so its
 * explanation differs from the page/page-library Viewer.
 */
const INSTANCE_VIEWER_DETAILS = {
    summary: "Baseline access to enter this instance.",
    capabilities: [
        "Open the instance home page and navigation",
        "Use search, favorites, and recent views",
        "View content they are separately granted access to (by Page Library or Page)",
        "Required to access the instance at all, unless it is marked publicly viewable"
    ]
};

export const PermissionLevelInfo = (props: { level: EntityPermissionType; scope?: EntityType }) => {
    const details = (props.level === EntityPermissionType.Viewer && props.scope === EntityType.Instance)
        ? INSTANCE_VIEWER_DETAILS
        : PERMISSION_LEVEL_DETAILS[props.level];
    if (!details) return null;
    return (
        <Popover
            dismissAriaLabel="Close"
            header={`${props.level} permission`}
            size="medium"
            triggerType="text"
            content={
                <SpaceBetween direction="vertical" size="xs">
                    <TextContent>
                        <p>{details.summary}</p>
                    </TextContent>
                    <TextContent>
                        <ul>
                            {details.capabilities.map((capability, index) => (
                                <li key={index}>{capability}</li>
                            ))}
                        </ul>
                    </TextContent>
                </SpaceBetween>
            }
        >
            <Link variant="info">Info</Link>
        </Popover>
    );
};
